import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './Carousel.scss';

// pause for in-browser testing
const Carousel = ({ items = [], autoplay = true, interval = 4000 }) => {
    const [index, setIndex] = useState(0);
    const containerRef = useRef(null);
    const startX = useRef(0);
    const deltaX = useRef(0);
    const isPaused = useRef(false);
    const isVisible = useRef(true);

    const prev = useCallback(() => setIndex((i) => (i - 1 + items.length) % items.length), [items.length]);
    const next = useCallback(() => setIndex((i) => (i + 1) % items.length), [items.length]);

    // touch handlers for swipe
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const onTouchStart = (e) => {
            isPaused.current = true; // pause autoplay on interaction
            startX.current = e.touches[0].clientX;
            deltaX.current = 0;
        };

        const onTouchMove = (e) => {
            deltaX.current = e.touches[0].clientX - startX.current;
        };

        const onTouchEnd = () => {
            if (Math.abs(deltaX.current) > 50) {
                if (deltaX.current < 0) next();
                else prev();
            }
            deltaX.current = 0;
            // resume after short delay
            setTimeout(() => (isPaused.current = false), 1000);
        };

        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchmove', onTouchMove, { passive: true });
        el.addEventListener('touchend', onTouchEnd);

        return () => {
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', onTouchEnd);
        };
    }, [items.length, next, prev]);

    // autoplay
    useEffect(() => {
        if (!autoplay || items.length <= 1) return;
        const id = setInterval(() => {
            // only advance when not paused and carousel is visible in viewport
            if (!isPaused.current && isVisible.current) next();
        }, interval);
        return () => clearInterval(id);
    }, [autoplay, interval, items.length, next]);

    // IntersectionObserver: track whether carousel is visible in viewport
    useEffect(() => {
        const el = containerRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    isVisible.current = entry.isIntersecting;
                });
            },
            { threshold: 0.5 }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Pause/resume on focus/hover (keyboard navigation intentionally disabled)
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const onMouseEnter = () => (isPaused.current = true);
        const onMouseLeave = () => (isPaused.current = false);
        const onFocusIn = () => (isPaused.current = true);
        const onFocusOut = () => (isPaused.current = false);

        el.addEventListener('mouseenter', onMouseEnter);
        el.addEventListener('mouseleave', onMouseLeave);
        el.addEventListener('focusin', onFocusIn);
        el.addEventListener('focusout', onFocusOut);

        return () => {
            el.removeEventListener('mouseenter', onMouseEnter);
            el.removeEventListener('mouseleave', onMouseLeave);
            el.removeEventListener('focusin', onFocusIn);
            el.removeEventListener('focusout', onFocusOut);
        };
    }, [items.length]);

    if (!items || items.length === 0) return null;

    return (
        <div
            className="carousel"
            ref={containerRef}
            role="region"
            aria-roledescription="carousel"
            aria-label="Projects carousel"
        >
            <div className="carousel__track" id="carousel-track">
                {items.map((it, i) => (
                    <div
                        key={i}
                        className={`carousel__slide ${i === index ? 'is-active' : ''}`}
                        aria-hidden={i === index ? 'false' : 'true'}
                        role="group"
                        aria-roledescription="slide"
                        aria-label={`${i + 1} of ${items.length}`}
                    >
                        {it.slug ? (
                            <Link
                                to={`/projects/${it.slug}`}
                                className="carousel__link"
                                aria-label={`View project: ${it.caption}`}
                            >
                                <figure className="carousel__figure">
                                    <img src={it.src} alt={it.alt || ''} />
                                    <figcaption className="carousel__caption">{it.caption}</figcaption>
                                </figure>
                            </Link>
                        ) : (
                            <figure className="carousel__figure">
                                <img src={it.src} alt={it.alt || ''} />
                                <figcaption className="carousel__caption">{it.caption}</figcaption>
                            </figure>
                        )}
                    </div>
                ))}
            </div>

            <button
                className="carousel__nav carousel__nav--prev"
                onClick={() => {
                    isPaused.current = true;
                    prev();
                    setTimeout(() => (isPaused.current = false), 1000);
                }}
                aria-label="Previous slide"
                aria-controls="carousel-track"
            >
                <span aria-hidden="true">‹</span>
            </button>
            <button
                className="carousel__nav carousel__nav--next"
                onClick={() => {
                    isPaused.current = true;
                    next();
                    setTimeout(() => (isPaused.current = false), 1000);
                }}
                aria-label="Next slide"
                aria-controls="carousel-track"
            >
                <span aria-hidden="true">›</span>
            </button>

            <div className="carousel__dots">
                {items.map((_, i) => (
                    <button
                        key={i}
                        className={`carousel__dot ${i === index ? 'is-active' : ''}`}
                        onClick={() => {
                            setIndex(i);
                            isPaused.current = true;
                            setTimeout(() => (isPaused.current = false), 1000);
                        }}
                        aria-label={`Go to slide ${i + 1}`}
                        aria-pressed={i === index}
                    />
                ))}
            </div>

            {/* Live region for screen readers announcing slide changes */}
            <div className="carousel__live" aria-live="polite" aria-atomic="true">
                {`${index + 1} of ${items.length}: ${items[index].caption}`}
            </div>
        </div>
    );
};

export default Carousel;
